/**
 * Default subjects and bodies for 書類審査結果メール (standalone copy; not shared with invitation Step 3).
 */
import type {
  ChatFlowDocumentItem,
  ChatFlowFormToFillItem,
  ChatFlowProfileSectionItem,
} from '../../../../../services/chatFlowApi';
import { requiresOriginalToHeadOffice } from '../../../../../catalog/variousCopyDocCodes';

export type ResubmissionEmailMode = 'approved' | 'rejected';

function defaultEmployeeAppUrl(): string {
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/emp`;
  }
  return '{WebサイトURL}';
}

export function formatDateJp(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return isoDate;
  return `${y}年${parseInt(m, 10)}月${parseInt(d, 10)}日`;
}

export const approvedTemplate = {
  subject: '【書類審査】ご提出書類はすべて承認されました（{受信者名}様）',
  body: `西行土木株式会社でございます。
書類審査の結果をご連絡いたします。

ご提出いただいた書類は、すべて承認いたしました。
ご対応ありがとうございました。

{原本送付案内}
【Webシステム】
下記リンクよりアクセスできます。
▶ システムはこちら：{WebサイトURL}

ご不明な点がございましたら、お問い合わせください。

※本メールは自動送信です。`,
};

export const rejectedTemplate = {
  subject: '【再提出のお願い】書類審査の結果（{受信者名}様）',
  body: `西行土木株式会社でございます。
書類審査の結果をご連絡いたします。

一部の書類について、再提出をお願いいたします。
詳細は下記をご確認ください。

【再提出が必要な書類】
{却下一覧}

再提出期限：{締切日}

【Webシステム】
下記リンクより再提出手続きへお進みください。
▶ システムはこちら：{WebサイトURL}

ご不明な点がございましたら、お問い合わせください。

※本メールは自動送信です。`,
};

const SUBJECT_PLACEHOLDER_WARNING_APPROVED =
  '以下のプレースホルダーは変更・削除しないでください: {受信者名}';
const BODY_PLACEHOLDER_WARNING_APPROVED =
  '以下のプレースホルダーは変更・削除しないでください: {WebサイトURL}, {原本送付案内}';
const SUBJECT_PLACEHOLDER_WARNING_REJECTED =
  '以下のプレースホルダーは変更・削除しないでください: {受信者名}, {締切日}';
const BODY_PLACEHOLDER_WARNING_REJECTED =
  '以下のプレースホルダーは変更・削除しないでください: {締切日}, {WebサイトURL}, {却下一覧}';

export function getPlaceholderWarnings(mode: ResubmissionEmailMode, target: 'subject' | 'body'): string {
  if (mode === 'approved') {
    return target === 'subject' ? SUBJECT_PLACEHOLDER_WARNING_APPROVED : BODY_PLACEHOLDER_WARNING_APPROVED;
  }
  return target === 'subject' ? SUBJECT_PLACEHOLDER_WARNING_REJECTED : BODY_PLACEHOLDER_WARNING_REJECTED;
}

function formHasCompletedRound(form: ChatFlowFormToFillItem): boolean {
  return (form.roundsBySubmission ?? []).some((r) => r.status === 'completed');
}

/**
 * Approved only when every submitted various-copy doc and every completed fill-in form round is approved.
 */
export function getResubmissionEmailMode(
  documents: ChatFlowDocumentItem[],
  forms: ChatFlowFormToFillItem[] = [],
  profileSections: ChatFlowProfileSectionItem[] = []
): ResubmissionEmailMode {
  const submitted = documents.filter((d) => d.status === 'submitted');
  const formsWithRounds = forms.filter(formHasCompletedRound);
  const profileNeedingReview = profileSections.filter(
    (s) => s.status === 'completed' || s.status === 'final_confirm'
  );
  const allDocsOk = submitted.length === 0 || submitted.every((d) => d.adminApproved === true);
  const allFormsOk =
    formsWithRounds.length === 0 || formsWithRounds.every((f) => f.adminApproved === true);
  const allProfileOk =
    profileNeedingReview.length === 0 ||
    profileNeedingReview.every((s) => s.adminApproved === true);
  return allDocsOk && allFormsOk && allProfileOk ? 'approved' : 'rejected';
}

function buildRejectedList(
  documents: ChatFlowDocumentItem[],
  forms: ChatFlowFormToFillItem[] = [],
  profileSections: ChatFlowProfileSectionItem[] = []
): string {
  const docLines = documents
    .filter((d) => d.status === 'submitted' && d.adminApproved === false)
    .map((d) => {
      const name = d.nameJa ?? d.name ?? d.docCode;
      const comment =
        d.adminComment != null && d.adminComment.trim() !== ''
          ? d.adminComment.trim()
          : '（コメントなし）';
      return `・${name}\n　コメント：${comment}`;
    });

  const formLines = forms
    .filter((f) => formHasCompletedRound(f) && f.adminApproved === false)
    .map((f) => {
      const name = f.nameJa ?? f.name ?? f.formCode;
      const comment =
        f.adminComment != null && f.adminComment.trim() !== ''
          ? f.adminComment.trim()
          : '（コメントなし）';
      return `・${name}\n　コメント：${comment}`;
    });

  const profileLines = profileSections
    .filter(
      (s) =>
        (s.status === 'completed' || s.status === 'final_confirm') && s.adminApproved === false
    )
    .map((s) => {
      const name = s.nameJa ?? s.name ?? s.section;
      const comment =
        s.adminComment != null && s.adminComment.trim() !== ''
          ? s.adminComment.trim()
          : '（コメントなし）';
      return `・${name}\n　コメント：${comment}`;
    });

  return [...docLines, ...profileLines, ...formLines].join('\n\n');
}

/** Builds the original-to-HQ reminder block for approval / submission-complete mail. */
export function buildOriginalMailReminder(documents: ChatFlowDocumentItem[]): string {
  const names = documents
    .filter(
      (d) =>
        requiresOriginalToHeadOffice(d.docCode) &&
        (d.status === 'submitted' || d.status === 'notyet' || Boolean(d.filePath))
    )
    .map((d) => d.nameJa ?? d.name ?? d.docCode)
    .filter((n, i, arr) => arr.indexOf(n) === i);
  if (names.length === 0) return '';
  const lines = names.map((n) => `・${n}`).join('\n');
  return `【原本の送付について】
以下の書類は、システムへのアップロードに加え、原本を本社へご送付ください。
${lines}

`;
}

function applyCommonPlaceholders(text: string, employeeName: string, deadlineIso: string | null): string {
  const web = defaultEmployeeAppUrl();
  let out = text.replace(/\{受信者名\}/g, employeeName || 'ご担当者');
  out = out.replace(/\{WebサイトURL\}/g, web);
  if (deadlineIso != null) {
    out = out.replace(/\{締切日\}/g, formatDateJp(deadlineIso));
  }
  return out;
}

export interface BuiltResubmissionEmail {
  mode: ResubmissionEmailMode;
  subject: string;
  body: string;
  deadlineIso: string | null;
}

/** Raw subject/body from settings API (or fall back to {@link approvedTemplate} / {@link rejectedTemplate}). */
export interface ResubmissionTemplatesOverride {
  approved: { subject: string; body: string };
  rejected: { subject: string; body: string };
}

export function buildResubmissionEmailContent(
  documents: ChatFlowDocumentItem[],
  employeeName: string,
  templates?: ResubmissionTemplatesOverride,
  forms: ChatFlowFormToFillItem[] = [],
  profileSections: ChatFlowProfileSectionItem[] = []
): BuiltResubmissionEmail {
  const approvedSrc = templates?.approved ?? { subject: approvedTemplate.subject, body: approvedTemplate.body };
  const rejectedSrc = templates?.rejected ?? { subject: rejectedTemplate.subject, body: rejectedTemplate.body };

  const mode = getResubmissionEmailMode(documents, forms, profileSections);
  const d = new Date();
  d.setDate(d.getDate() + 14);
  const defaultDeadline = d.toISOString().slice(0, 10);

  if (mode === 'approved') {
    const subject = applyCommonPlaceholders(approvedSrc.subject, employeeName, null);
    let body = applyCommonPlaceholders(approvedSrc.body, employeeName, null);
    const reminder = buildOriginalMailReminder(documents);
    if (body.includes('{原本送付案内}')) {
      body = body.replace(/\{原本送付案内\}/g, reminder);
    } else if (reminder) {
      body = body.replace(/【Webシステム】/, `${reminder}【Webシステム】`);
      if (!body.includes(reminder.trim())) {
        body = `${body.trimEnd()}\n\n${reminder}`;
      }
    }
    return { mode, subject, body, deadlineIso: null };
  }

  const rejectedList = buildRejectedList(documents, forms, profileSections);
  let subject = applyCommonPlaceholders(rejectedSrc.subject, employeeName, defaultDeadline);
  let body = applyCommonPlaceholders(rejectedSrc.body, employeeName, defaultDeadline);
  body = body.replace(/\{却下一覧\}/g, rejectedList || '（該当なし）');
  body = body.replace(/\{原本送付案内\}/g, '');
  return { mode, subject, body, deadlineIso: defaultDeadline };
}

/** Updates the 再提出期限 line when the date picker changes (rejected flow). */
export function replaceDeadlineLineInBody(body: string, deadlineIso: string): string {
  const formatted = formatDateJp(deadlineIso);
  return body.replace(/再提出期限：[^\n\r]*/m, `再提出期限：${formatted}`);
}
